import { Pallet_t, Box_t, Layout_t, rotateBox90Degrees } from 'blackbox';
import { UnitsSystem } from 'libapi';
import { useState, useEffect, useMemo, useRef } from 'react';
import { COLOR, roundToNearestDecimal } from '../../../shared/shared';
import {
	ModelBox,
	PALLETCORNERS,
	getModelData,
	Rect,
	getFractionalCoordinates,
	CornerNumber,
	isRectWithinBounds,
	calculateMultipickDimensions,
} from '../../layout_utils';
import { getDeltaString } from '../utilities/helpers';
import { SelectMode, ModelRect, LayoutBounds } from '../utilities/types';
import { DraggableRect, DraggableRectProps } from './DraggableRectangle';

export interface LayoutModelProps {
	pallet: Pallet_t;
	size: number;
	updateModelBox?: (bpo: ModelBox, index: number) => void;
	boxes?: ModelBox[];
	corner?: PALLETCORNERS;
	enableDrag?: boolean;
	showName?: boolean;
	forceCornerNumber?: number;
	allBoxes: Box_t[];
	selectedBoxIndices?: number[];
	selectBoxes?: (i: number[]) => void;
	deselectBox?: (i: number) => void;
	selectMode?: SelectMode;
	gapDistance?: number;
	snapStrength: number;
	unitsSystem: UnitsSystem;
	overhang: Layout_t['overhang'];
	moveBoxes?: boolean;
}

export function LayoutModel({
	enableDrag,
	selectedBoxIndices,
	pallet,
	size,
	boxes,
	updateModelBox,
	corner,
	showName,
	allBoxes,
	forceCornerNumber,
	selectMode,
	deselectBox,
	gapDistance,
	snapStrength,
	unitsSystem,
	overhang,
	selectBoxes,
	moveBoxes,
}: LayoutModelProps) {
	const rotateBox = (index: number): void => {
		if (!boxes || !updateModelBox) return;
		const rotatedBox = rotateBox90Degrees({ ...boxes[index] });
		updateModelBox(rotatedBox, index);
	};

	const lSize = size;
	const [everyBox, setEveryBox] = useState<Box_t[]>(allBoxes),
		[BoxSVGs, setBoxSVGs] = useState<ModelRect[]>([]);
	useEffect(() => {
		setEveryBox(allBoxes);
	}, [allBoxes]);

	let isDragEnabled = !!enableDrag;

	const hasGap = gapDistance !== undefined;
	const overhangIncludingGap = isNaN(overhang)
		? 0
		: overhang + (hasGap ? gapDistance / 2 : 0);
	const modelData = getModelData(pallet, lSize, overhangIncludingGap),
		{ w, l, norm, scale, topX, topY, overhangData } = modelData;

	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(
		null
	);
	const [dragCurrent, setDragCurrent] = useState<{
		x: number;
		y: number;
	} | null>(null);
	const svgRef = useRef<SVGSVGElement>(null);

	const svg_props = {
		width: w,
		height: l,
		x: topX,
		y: topY,
	};

	const isBoxWithinSelection = (
		boxRect: { x: number; y: number; width: number; height: number },
		selectionRect: { x: number; y: number; width: number; height: number }
	) => {
		return (
			boxRect.x < selectionRect.x + selectionRect.width &&
			boxRect.x + boxRect.width > selectionRect.x &&
			boxRect.y < selectionRect.y + selectionRect.height &&
			boxRect.y + boxRect.height > selectionRect.y
		);
	};

	const handlePointerDown = (e: React.PointerEvent) => {
		if (selectMode !== SelectMode.Multi || moveBoxes) return;
		if (e.button !== 0) return; // Only respond to left-click

		const svgEvent = e.nativeEvent as unknown as React.PointerEvent;
		const { x, y } = getSVGCoordinates(svgEvent);
		if (x < svg_props.x) return;
		setIsDragging(true);
		setDragStart({ x, y });
		setDragCurrent({ x, y });
	};

	const getSVGCoordinates = (
		e: React.PointerEvent
	): { x: number; y: number } => {
		if (!svgRef.current) return { x: 0, y: 0 };
		const svg = svgRef.current;
		const point = svg.createSVGPoint();
		point.x = e.clientX;
		point.y = e.clientY;

		const ctm = svg.getScreenCTM();
		if (!ctm) return { x: 0, y: 0 };

		const svgCoords = point.matrixTransform(ctm.inverse());
		return { x: svgCoords.x, y: svgCoords.y };
	};

	const handlePointerUp = () => {
		if (!isDragging || !dragStart || !dragCurrent) {
			setIsDragging(false);
			setDragStart(null);
			setDragCurrent(null);
			return;
		}

		if (!selectionRect) {
			setIsDragging(false);
			setDragStart(null);
			setDragCurrent(null);
			return;
		}

		// Check which boxes are within the selection rectangle
		const selected = BoxSVGs?.map((svg, index) => {
			let width = svg.width,
				height = svg.height;
			if (svg.multipick) {
				if (svg.multipickAlongWidth) width *= svg.multipick + 1;
				else height *= svg.multipick + 1;
				if (svg.rotated) [width, height] = [height, width];
			}
			return isBoxWithinSelection(
				{ ...svg, width: width, height: height },
				selectionRect
			)
				? index
				: -1;
		}).filter(index => index !== -1);

		// Apply selection
		if (selectBoxes) {
			selectBoxes(selected);
		}

		// Reset dragging state AFTER computation
		setIsDragging(false);
		setDragStart(null);
		setDragCurrent(null);
	};

	useEffect(() => {
		const handlePointerMove = (e: React.PointerEvent) => {
			if (!isDragging || !dragStart) return;

			// Get SVG-relative coordinates
			const { x, y } = getSVGCoordinates(e);

			// Constrain the drag rectangle within bounds
			const constrainedX = Math.min(
				Math.max(x, svg_props.x),
				svg_props.width + svg_props.x
			);
			const constrainedY = Math.min(
				Math.max(y, svg_props.y),
				svg_props.height + svg_props.y
			);

			// Update dragCurrent
			setDragCurrent({ x: constrainedX, y: constrainedY });
		};

		if (isDragging) {
			const handleMove = (e: PointerEvent) =>
				handlePointerMove(e as unknown as React.PointerEvent);
			const handleUp = (e: PointerEvent) => handlePointerUp();

			window.addEventListener('pointermove', handleMove);
			window.addEventListener('pointerup', handleUp);

			return () => {
				window.removeEventListener('pointermove', handleMove);
				window.removeEventListener('pointerup', handleUp);
			};
		}
	}, [isDragging, handlePointerUp]);

	const selectionRect = useMemo(() => {
		if (!isDragging || !dragStart || !dragCurrent) return null;
		const x = Math.min(dragStart.x, dragCurrent.x);
		const y = Math.min(dragStart.y, dragCurrent.y);
		const width = Math.abs(dragCurrent.x - dragStart.x);
		const height = Math.abs(dragCurrent.y - dragStart.y);
		return { x, y, width, height };
	}, [isDragging, dragStart, dragCurrent]);

	const lScaleFactor = useMemo(
		() => (lSize * scale) / norm,
		[norm, scale, lSize]
	);

	let logColor: string = String(COLOR.LOG);

	const lGap = (gapDistance || 0) * lScaleFactor;

	const plankWidth = svg_props.width / 10;
	const leftLog: Rect = {
		x: 0,
		y: 0,
		width: plankWidth,
		height: svg_props.height,
		fill: logColor,
		stroke: logColor,
		strokeWidth: 0,
	};
	const rightLog: Rect = {
		x: svg_props.width - plankWidth,
		y: 0,
		width: plankWidth,
		height: svg_props.height,
		fill: logColor,
		stroke: logColor,
		strokeWidth: 0,
	};
	const dragBounds: LayoutBounds = {
		min: {
			x: roundToNearestDecimal(overhangData.x, 1),
			y: roundToNearestDecimal(overhangData.y, 1),
		},
		max: {
			x: roundToNearestDecimal(
				overhangIncludingGap
					? overhangData.x + overhangData.width
					: svg_props.width + svg_props.x,
				1
			),
			y: roundToNearestDecimal(
				overhangIncludingGap
					? overhangData.y + overhangData.length
					: svg_props.height + svg_props.y,
				1
			),
		},
	};

	const planks = useMemo(
			function () {
				const plankColor: string = String(COLOR.PLANK),
					totalGap = svg_props.height * 0.3,
					numPlanks = Math.floor((svg_props.height - totalGap) / plankWidth),
					gap = (svg_props.height - numPlanks * plankWidth) / (numPlanks - 1),
					increment = plankWidth + gap;
				const lTemp: Rect[] = [];
				for (let i = 0; i < numPlanks; i++) {
					let plk: Rect = {
						x: 0,
						y: i * increment,
						width: svg_props.width,
						height: plankWidth,
						fill: plankColor,
						stroke: plankColor,
						strokeWidth: 0,
					};
					lTemp.push(plk);
				}
				return lTemp;
			},
			[plankWidth, svg_props.height, svg_props.width]
		),
		lGetFractionalCoordinates = function (
			pX: number,
			pY: number
		): [number, number] {
			return getFractionalCoordinates(modelData, lSize, lSize, pX, pY);
		},
		[lDeltaDisplay, lSetDeltaDisplay] = useState<string>(''),
		lUpdateDelta = function (pBoxIndex: number, pX: number, pY: number) {
			const lModelBox = BoxSVGs[pBoxIndex],
				[lOldX, lOldY] = lGetFractionalCoordinates(
					lModelBox.x + lModelBox.width / 2,
					lModelBox.y + lModelBox.height / 2
				),
				[lNewX, lNewY] = lGetFractionalCoordinates(
					pX + lModelBox.width / 2,
					pY + lModelBox.height / 2
				),
				lDeltaX = pallet.Dimensions.width * (lNewX - lOldX),
				lDeltaY = -1 * pallet.Dimensions.length * (lNewY - lOldY);
			lSetDeltaDisplay(
				getDeltaString(lDeltaX, lDeltaY, lNewX, lNewY, unitsSystem)
			);
		},
		updateRectPosition = (index: number, nx: number, ny: number) => {
			if (boxes && updateModelBox) {
				const [fractionX, fractionY] = lGetFractionalCoordinates(nx, ny);
				let box = boxes[index];

				let newBox = {
					...box,
					position: {
						x: fractionX,
						y: fractionY,
					},
				} as ModelBox;
				updateModelBox(newBox, index);
				lSetDeltaDisplay('');
			}
		};

	useEffect(() => {
		if (!boxes) return;
		const svgs = boxes.map((b: ModelBox, i: number) => {
			const selected: boolean =
				selectedBoxIndices !== undefined && selectedBoxIndices.indexOf(i) >= 0;
			const { position, rotated, multipick, multipickAlongWidth } = b;
			const box = everyBox[b.index];

			const x = w * position.x + topX;
			const y = l * position.y + topY;
			let { width, length } = box.dimensions;
			const SAFETY_FACTOR = 0.01;
			width += width * SAFETY_FACTOR;
			length += length * SAFETY_FACTOR;
			width *= lScaleFactor;
			length *= lScaleFactor;

			const boxColor = selected
				? String(COLOR.SELECTED_BOX)
				: String(COLOR.CLEAR_BOX);
			const strokeColor = String(COLOR.CARDBOARD);
			const rectProps: Rect = {
				x,
				y,
				width: rotated ? length : width,
				height: rotated ? width : length,
				fill: boxColor,
				stroke: strokeColor,
				strokeWidth: 1,
			};

			const { width: rectMultipickWidth, height: rectMultipickHeight } =
				calculateMultipickDimensions({
					width,
					height: length,
					multipick,
					multipickAlongWidth,
					rotated,
				});
			const rectPropsWithGap = {
				x: x - lGap / 2,
				y: y - lGap / 2,
				width: rectMultipickWidth + lGap,
				height: rectMultipickHeight + lGap,
			};
			const isBoxWithinBounds = isRectWithinBounds(rectPropsWithGap, dragBounds);

			const boxprops: ModelRect = {
				...rectProps,
				fill: isBoxWithinBounds ? boxColor : COLOR.RED_CLEAR,
				selected,
				rotated,
				multipick,
				multipickAlongWidth,
				spin: b.spin,
				labelSide: box.labelSide,
			};
			return boxprops;
		});
		setBoxSVGs(svgs);
	}, [
		boxes,
		selectedBoxIndices,
		lSize,
		everyBox,
		l,
		lScaleFactor,
		topX,
		topY,
		selectMode,
		w,
	]);

	function getSelectedBoxes() {
		if (!boxes || !selectedBoxIndices) return [];
		return boxes
			.map((_: ModelBox, i: number) => {
				if (selectedBoxIndices.indexOf(i) >= 0) return i;
				return -1;
			})
			.filter((i: number) => {
				return i >= 0;
			});
	}

	function handleGroupDrag(i: number, dx: number, dy: number) {
		const selected = getSelectedBoxes();
		if (selected.length <= 1 || !moveBoxes) return false; // not a group.
		const cp = [...BoxSVGs];
		selected.forEach((index: number) => {
			if (i === index) return;
			const nb = { ...cp[index] };
			nb.x -= dx;
			nb.y -= dy;
			cp[index] = nb;
		});
		setBoxSVGs(cp);
		return true;
	}

	const outerSVG = {
		x: 0,
		y: 0,
		width: lSize,
		height: lSize,
	} as any;
	let cornerCircleProps = {} as any,
		cornerTextProps = {} as any;

	if (corner) {
		let green = 'rgb(91,196,126)';
		cornerCircleProps.r = 16;
		cornerCircleProps.fill = green;
		cornerTextProps.height = 32;
		cornerTextProps.width = 32;
		cornerTextProps.fontSize = 16;
		cornerTextProps.stroke = 'white';
		cornerTextProps.fill = 'white';

		switch (corner) {
			case PALLETCORNERS.TOP_LEFT: {
				cornerCircleProps.cx = svg_props.x;
				cornerCircleProps.cy = svg_props.y;
				break;
			}
			case PALLETCORNERS.BOTTOM_LEFT: {
				cornerCircleProps.cx = svg_props.x;
				cornerCircleProps.cy = svg_props.height + svg_props.y;
				break;
			}
			case PALLETCORNERS.BOTTOM_RIGHT: {
				cornerCircleProps.cx = svg_props.width + svg_props.x;
				cornerCircleProps.cy = svg_props.height + svg_props.y;
				break;
			}
		}
		cornerCircleProps.cx += outerSVG.x;
		cornerCircleProps.cy += outerSVG.y;

		cornerTextProps.x = cornerCircleProps.cx - 5.5;
		cornerTextProps.y = cornerCircleProps.cy + 5.5;
	}

	const snapParams = {
			xl: topX,
			xh: topX + w,
			yl: topY,
			yh: topY + l,
		},
		lDeselectBox = deselectBox
			? deselectBox
			: function (i: number) {
					return;
			  };

	return (
		<div className="flex-col">
			<svg {...outerSVG} onPointerDown={handlePointerDown} ref={svgRef}>
				{overhang > 0 && (
					<rect
						x={overhangData.x}
						y={overhangData.y}
						width={overhangData.width}
						height={overhangData.length}
						fill="none"
						stroke={COLOR.DARK_BLUE}
						strokeWidth={3}
						strokeDasharray="10 5"
					/>
				)}
				<svg {...svg_props}>
					<rect {...leftLog} />
					<rect {...rightLog} />
					{planks.map((r: Rect, index: number) => {
						return <rect {...r} key={index} />;
					})}
				</svg>
				{boxes &&
					boxes.length > 0 &&
					BoxSVGs.map((r: ModelRect, index: number) => {
						if (index >= boxes.length) return;
						let otherRects: ModelRect[] = [];
						let otherRectIndexes: number[] = [];
						const groupIndexes: number[] = [];
						const indexes = getSelectedBoxes();

						BoxSVGs.forEach((r, i) => {
							if (i !== index) {
								otherRects.push(r);
								otherRectIndexes.push(i);
								if (indexes.includes(i)) {
									groupIndexes.push(otherRectIndexes.length - 1);
								}
							}
						});
						const dp: DraggableRectProps = {
							index,
							rect: r,
							gapDistance: lGap,
							handleGroupDrag,
							otherRects,
							updatePosition: updateRectPosition,
							enabled: isDragEnabled,
							snapStrength,
							pUpdateDisplayPosition: lUpdateDelta,
							onDoubleClick: rotateBox,
							selectMode,
							dragBounds,
							groupIndexes,
							youAreInGroup:
								selectMode === SelectMode.Multi &&
								(selectedBoxIndices?.includes(index) || false),
							...snapParams,
						};
						return <DraggableRect key={index} {...dp} />;
					})}
				{isDragging && dragStart && dragCurrent && (
					<rect
						x={selectionRect?.x || 0}
						y={selectionRect?.y || 0}
						width={selectionRect?.width || 0}
						height={selectionRect?.height || 0}
						fill="rgba(24, 144, 255, 0.2)"
						stroke="#1890ff"
						strokeDasharray="4"
					/>
				)}
			</svg>
			{corner && <circle {...cornerCircleProps} />}
			{corner && (
				<text {...cornerTextProps}>
					{forceCornerNumber ? '1' : String(CornerNumber(corner) + 1)}
				</text>
			)}
			{(isDragEnabled || selectMode === SelectMode.Multi) && (
				<span className="normal-font" style={{ height: '16pt', paddingTop: '8pt' }}>
					{lDeltaDisplay}
				</span>
			)}
		</div>
	);
}
